// The Android app's device key, as the web side sees it.
//
// The native app generates a 256-bit random key (base64url, no padding, 43
// characters) and hands it to its own Trusted Web Activity in the URL
// fragment as `cbdk=<key>`. The inline <head> bootstrap in index.html
// (scripts/device-key-es5.mjs) takes it off the URL before any other script
// runs and, inside the app only, stores it in localStorage under
// DEVICE_KEY_STORAGE_KEY. This module only READS it.
//
// What the key is for: a Play link token (POST /pro/play/link-token) is bound
// to sha256(key), so only THIS phone's app (which holds the key itself) can
// resolve it. The key never leaves this origin's storage except as that hash.
//
// The bootstrap sets window.__cbBillingOff when it saw a key it could not
// store (storage threw) or could not use (malformed). The stored copy, if
// any, may then belong to an older install, so billing is off for the rest of
// this document.

export const DEVICE_KEY_PARAM = 'cbdk'
export const DEVICE_KEY_STORAGE_KEY = 'cb.deviceKey'
export const BILLING_OFF_MARKER = '__cbBillingOff'

const KEY_SHAPE = /^[A-Za-z0-9_-]{43}$/

/** A well-formed device key: 43 base64url characters. */
export function validDeviceKey(key: unknown): key is string {
  return typeof key === 'string' && KEY_SHAPE.test(key)
}

/** True when the head bootstrap turned billing off for this document. */
export function billingMarkedOff(): boolean {
  if (typeof window === 'undefined') return true
  return (window as unknown as Record<string, unknown>)[BILLING_OFF_MARKER] === 1
}

/** The stored device key, or null (absent, malformed, unreadable storage, or marked off). */
export function readDeviceKey(): string | null {
  if (billingMarkedOff()) return null
  try {
    const key = window.localStorage.getItem(DEVICE_KEY_STORAGE_KEY)
    return validDeviceKey(key) ? key : null
  } catch {
    return null
  }
}

/** Lowercase hex SHA-256 of the key's ASCII string (64 characters), per the wire contract. */
export async function deviceKeyHash(key: string): Promise<string> {
  // eslint-disable-next-line i18next/no-literal-string -- algorithm name
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(key))
  let hex = ''
  for (const b of new Uint8Array(digest)) hex += b.toString(16).padStart(2, '0')
  return hex
}
